import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import {
  getApiErrorMessage,
  getHistoricoAtendimentos,
  HistoricoAtendimento,
} from "src/apis/prontuario";
import { getStoredToken, getUserId } from "src/utils/auth";
import { formatDateToBR } from "src/utils/date";
import { Id } from "src/utils/types";
import SkeletonLoad from "../SkeletonLoad";
import { formatEmpty } from "./utils";

type ClinicalHistoryProps = {
  idColaborador: Id;
  initialHistory?: HistoricoAtendimento[];
  onNotify: (message: string, severity?: "success" | "error" | "info" | "warning") => void;
};

const getPsicologoName = (history: HistoricoAtendimento): string => {
  if (history.psicologo && typeof history.psicologo === "object") {
    return formatEmpty(history.psicologo.name);
  }
  return formatEmpty(history.psicologo ?? history.psychologist_name);
};

const getEmpresaName = (history: HistoricoAtendimento): string => {
  if (history.empresa && typeof history.empresa === "object") {
    return formatEmpty(history.empresa.trade_name ?? history.empresa.nome);
  }
  return formatEmpty(history.empresa ?? history.trade_name);
};


const ClinicalHistory: FC<ClinicalHistoryProps> = ({ idColaborador, initialHistory, onNotify }) => {
  const [history, setHistory] = useState<HistoricoAtendimento[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [tokenVersion, setTokenVersion] = useState<number>(0);

  const currentPsychologistId = getUserId();

  const fetchHistory = useCallback(async () => {
    if (!idColaborador) {
      setHistory([]);
      return;
    }

    const token = getStoredToken();

    if (!token) {
      setHistory([]);
      onNotify("Token de autenticação não encontrado. Faça login novamente.", "error");
      return;
    }

    if (initialHistory && initialHistory.length > 0) {
      setHistory(initialHistory.filter(item => item.fk_psychologist_id === currentPsychologistId));
      return;
    }

    setLoading(true);
    setHistory([]);

    try {
      const response = await getHistoricoAtendimentos(idColaborador);
      setHistory(response.filter(item => item.fk_psychologist_id === currentPsychologistId));
    } catch (err: unknown) {
      setHistory([]);
      onNotify(
        getApiErrorMessage(err, "Não foi possível carregar o histórico de atendimentos."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [idColaborador, initialHistory, onNotify, currentPsychologistId]);

  useEffect(() => {
    const storageListener = () => setTokenVersion((previous) => previous + 1);
    window.addEventListener("storage", storageListener);

    return () => {
      window.removeEventListener("storage", storageListener);
    };
  }, []);

  useEffect(() => {
    setHistory([]);
    fetchHistory();
  }, [fetchHistory, idColaborador, tokenVersion]);

  if (loading) {
    return <SkeletonLoad variant="table" rows={5} columns={7} />;
  }

  if (history.length === 0) {
    return <Alert severity="info">Sem histórico de atendimentos.</Alert>;
  }

  return (
    <Box>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Data do atendimento</strong></TableCell>
              <TableCell><strong>Psicólogo</strong></TableCell>
              <TableCell><strong>Empresa</strong></TableCell>
              <TableCell><strong>Área</strong></TableCell>
              <TableCell><strong>Cidade</strong></TableCell>
              <TableCell><strong>Queixa original</strong></TableCell>
              <TableCell><strong>Anotações antigas</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((entry, index) => (
              <TableRow key={String(entry.id_atendimento ?? entry.id ?? index)}>
                <TableCell>{formatDateToBR(entry.date_of_service ?? entry.atendimento)}</TableCell>
                <TableCell>{getPsicologoName(entry)}</TableCell>
                <TableCell>{getEmpresaName(entry)}</TableCell>
                <TableCell>{formatEmpty(entry.area)}</TableCell>
                <TableCell>{formatEmpty(entry.cidade ?? entry.city)}</TableCell>
                <TableCell>{formatEmpty(entry.queixa_inicial ?? entry.complaint)}</TableCell>
                <TableCell>{formatEmpty(entry.observacoes ?? entry.annotations)}</TableCell>
                </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ClinicalHistory;
